'use client'
import { ArtistChannelsOptions, GenericResult, getArtists } from '@/app/util/channel-api'
import Grid from '@/components/grid/grid'
import UAParserContext from '@/context/ua-parser'
import { GifsResult } from '@giphy/js-fetch-api'
import { IChannel, IGif } from '@giphy/js-types'
import dynamic from 'next/dynamic'
import { useCallback, useContext, useState } from 'react'
import Filters from './filters'
import Freelance from './freelance'
import Sort from './sort'

const CutoutAttribution = dynamic(() => import('@/components/cutout-attribution'), { ssr: false })

const mapArtistGif = (artist: IChannel): IGif => {
    return {
        ...artist.featured_gif,
        url: artist.url,
    }
}

type Props = {
    channelResult: GenericResult<IChannel>
}

const desktopLimit = 10
const mobileLimit = 6
const ArtistContents = ({ channelResult }: Props) => {
    const { deviceType } = useContext(UAParserContext)
    const [options, setOptions] = useState<ArtistChannelsOptions>({})
    const isDefault = Object.keys(options).length === 0
    const isAlphabetical = options.sort === 'username'

    const fetchGifs = useCallback(
        async (offset: number): Promise<GifsResult> => {
            const limit = deviceType === 'desktop' ? desktopLimit : mobileLimit
            const nextChannels = await getArtists({ offset, limit, ...options })
            return {
                ...nextChannels,
                data: nextChannels.data.map(mapArtistGif),
            }
        },
        [deviceType, options]
    )

    const setFilter = (filter?: string) => {
        setOptions({
            ...options,
            filter,
        })
    }

    const setSort = (isAlphabetical: boolean) => {
        setOptions({
            ...options,
            order: isAlphabetical ? 'asc' : 'desc',
            sort: isAlphabetical ? 'username' : 'updatetime',
        })
    }

    const setFreelance = (freelance: boolean) => {
        setOptions({
            ...options,
            freelance,
        })
    }

    return (
        <>
            <Sort isAlphabetical={isAlphabetical} onChange={setSort}>
                {deviceType === 'mobile' ? null : (
                    <Freelance isFreelance={!!options.freelance} onChange={setFreelance} />
                )}
            </Sort>
            {isAlphabetical && <Filters filter={options.filter} onChange={setFilter} />}
            <Grid
                key={`${Object.values(options).join('-')}-${options.freelance}`}
                initialGifs={isDefault ? channelResult.data.map(mapArtistGif) : []}
                fetchGifs={fetchGifs}
                overlay={({ gif, isHovered }) => {
                    return <CutoutAttribution user={gif.user} isHovered={isHovered} canViewForHire />
                }}
                columns={deviceType === 'desktop' ? 3 : 2}
            />
        </>
    )
}

export default ArtistContents
